import React from 'react';
import { Accordion, Box, useMediaQuery } from '@hopskipdrive/react-components';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { AccordionDetails, AccordionSummary, Typography } from '@mui/material';
import { colorPalette, colorTokens, variables } from '@rideiq/shared/components/variables';

type SectionAccordionProps = {
  id: string;
  index: number;
  title: string;
  subtitle: string;
  isExpanded: boolean;
  onToggle: () => void;
  children: React.ReactNode;
};

export const SectionAccordion = ({
  id,
  index,
  title,
  subtitle,
  isExpanded,
  onToggle,
  children,
}: SectionAccordionProps) => (
  <Accordion
    id={`section-${id}`}
    expanded={isExpanded}
    onChange={onToggle}
    disableGutters
    elevation={0}
    data-testid={`section-accordion-${id}`}
    sx={{
      border: `1px solid ${colorPalette.neutralVariant90}`,
      borderRadius: '12px !important',
      mb: '2rem',
      // Mobile: 100px sticky headers (account bar + mobile nav) + 46px tabs bar + 8px buffer = 154px
      scrollMarginTop: useMediaQuery({ maxWidth: variables.breakpoints.desktop - 1 }) ? '154px' : '86px',
      '&:before': { display: 'none' },
    }}
  >
    <AccordionSummary
      expandIcon={<ExpandMoreIcon />}
      aria-controls={`${id}-content`}
      id={`${id}-header`}
      sx={{
        padding: '1.375rem',
        borderBottom: isExpanded ? `1px solid ${colorPalette.neutralVariant90}` : 'none',
        alignItems: 'center',
        margin: 0,
        '& .MuiAccordionSummary-content': { margin: 0 },
        // Preserve the Rider form chevron geometry over the shared Accordion default.
        '&& .MuiAccordionSummary-expandIconWrapper': {
          width: 'auto',
          '& > svg': { color: 'inherit' },
        },
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
        <Box
          sx={{
            width: 30,
            height: 30,
            borderRadius: '50%',
            bgcolor: colorTokens.primary.main,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          <Typography sx={{ color: colorTokens.text.contrast, fontSize: 14, lineHeight: 1 }}>{index}</Typography>
        </Box>
        <Box>
          <Typography sx={{ fontWeight: 700, fontSize: 16, color: colorTokens.text.dark, lineHeight: 1 }}>
            {title}
          </Typography>
          <Typography sx={{ fontSize: 14, color: colorPalette.neutralVariant60, mt: '4px' }}>{subtitle}</Typography>
        </Box>
      </Box>
    </AccordionSummary>
    <AccordionDetails sx={{ padding: '1.375rem' }}>{children}</AccordionDetails>
  </Accordion>
);
